/**
 * Offline Queue Manager
 * Queues operations when offline and syncs when back online
 * Uses localStorage for simplicity (no external dependencies)
 *
 * Race Condition Protection:
 * - Version tracking to detect concurrent modifications
 * - Retry logic for conflict resolution
 * - Storage events to sync across tabs
 */

import { showToast } from '../components/Toast'
import { logger } from './logger'

export type QueuedOperation = {
  id: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  endpoint: string
  data?: any
  timestamp: number
  retryCount: number
  entityType?: 'deal' | 'client' | 'prospect' | 'task' | 'contract' | 'invoice'
  entityId?: string
}

type QueueData = {
  version: number
  operations: QueuedOperation[]
}

const QUEUE_KEY = 'reppr_offline_queue'
const MAX_RETRY_ATTEMPTS = 3

// Generate unique ID
function generateId(): string {
  return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Get queue from localStorage with version
function getQueueData(): QueueData {
  try {
    const stored = localStorage.getItem(QUEUE_KEY)
    if (!stored) {
      return { version: 0, operations: [] }
    }

    const parsed = JSON.parse(stored)

    // Handle legacy format (array without version)
    if (Array.isArray(parsed)) {
      return { version: 1, operations: parsed }
    }

    return parsed as QueueData
  } catch {
    return { version: 0, operations: [] }
  }
}

// Get queue operations (backward compatibility)
function getQueue(): QueuedOperation[] {
  return getQueueData().operations
}

// Save queue to localStorage with version and conflict detection
function saveQueue(operations: QueuedOperation[], expectedVersion?: number): boolean {
  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      const current = getQueueData()

      // If expectedVersion is provided, check for conflicts
      if (expectedVersion !== undefined && current.version !== expectedVersion) {
        logger.warn('[OfflineQueue] Version conflict detected, retrying...')
        // Short delay before retry
        const delay = Math.pow(2, attempt) * 10 // Exponential backoff: 10ms, 20ms, 40ms
        const start = Date.now()
        while (Date.now() - start < delay) {
          // Busy wait (acceptable for short delays)
        }
        continue
      }

      const newData: QueueData = {
        version: current.version + 1,
        operations
      }

      localStorage.setItem(QUEUE_KEY, JSON.stringify(newData))
      return true
    } catch (err) {
      logger.error(`[OfflineQueue] Failed to save queue (attempt ${attempt + 1}):`, err)
      if (attempt === MAX_RETRY_ATTEMPTS - 1) {
        return false
      }
    }
  }
  return false
}

// Add operation to queue with conflict resolution
export async function queueOperation(
  operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retryCount'>
): Promise<string> {
  const id = generateId()

  const queuedOp: QueuedOperation = {
    ...operation,
    id,
    timestamp: Date.now(),
    retryCount: 0,
  }

  // Retry loop for concurrent modifications
  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
    const queueData = getQueueData()
    const newOperations = [...queueData.operations, queuedOp]

    const success = saveQueue(newOperations, queueData.version)
    if (success) {
      logger.debug('[OfflineQueue] Queued operation:', queuedOp)
      showToast('Saved offline. Will sync when online.', 'info')
      return id
    }

    // If we failed due to conflict, retry
    logger.debug(`[OfflineQueue] Queueing retry ${attempt + 1}/${MAX_RETRY_ATTEMPTS}`)
  }

  throw new Error('Failed to queue operation after multiple attempts')
}

// Get all queued operations
export async function getQueuedOperations(): Promise<QueuedOperation[]> {
  return getQueue().sort((a, b) => a.timestamp - b.timestamp)
}

// Get queue count
export async function getQueueCount(): Promise<number> {
  return getQueue().length
}

// Remove operation from queue with conflict resolution
export async function removeFromQueue(id: string): Promise<void> {
  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
    const queueData = getQueueData()
    const newOperations = queueData.operations.filter(op => op.id !== id)

    const success = saveQueue(newOperations, queueData.version)
    if (success) {
      return
    }

    logger.debug(`[OfflineQueue] Remove retry ${attempt + 1}/${MAX_RETRY_ATTEMPTS}`)
  }

  logger.error('[OfflineQueue] Failed to remove operation after multiple attempts')
}

// Update operation retry count with conflict resolution
export async function incrementRetryCount(id: string): Promise<void> {
  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
    const queueData = getQueueData()
    const newOperations = queueData.operations.map(op =>
      op.id === id ? { ...op, retryCount: op.retryCount + 1 } : op
    )

    const success = saveQueue(newOperations, queueData.version)
    if (success) {
      return
    }

    logger.debug(`[OfflineQueue] Increment retry ${attempt + 1}/${MAX_RETRY_ATTEMPTS}`)
  }

  logger.error('[OfflineQueue] Failed to increment retry count after multiple attempts')
}

// Clear all operations
export async function clearQueue(): Promise<void> {
  localStorage.removeItem(QUEUE_KEY)
}

// Process queue when online
export async function processQueue(
  onProgress?: (current: number, total: number) => void
): Promise<{
  successful: number
  failed: number
  errors: Array<{ operation: QueuedOperation; error: string }>
}> {
  if (!navigator.onLine) {
    logger.debug('[OfflineQueue] Still offline, skipping sync')
    return { successful: 0, failed: 0, errors: [] }
  }

  const operations = await getQueuedOperations()
  if (operations.length === 0) {
    return { successful: 0, failed: 0, errors: [] }
  }

  logger.debug(`[OfflineQueue] Processing ${operations.length} operations`)

  let successful = 0
  let failed = 0
  const errors: Array<{ operation: QueuedOperation; error: string }> = []

  for (let i = 0; i < operations.length; i++) {
    const op = operations[i]

    if (onProgress) {
      onProgress(i + 1, operations.length)
    }

    try {
      // Execute the operation
      const response = await fetch(op.endpoint, {
        method: op.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: op.data ? JSON.stringify(op.data) : undefined,
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      // Success - remove from queue
      await removeFromQueue(op.id)
      successful++
      logger.debug(`[OfflineQueue] ✓ Operation ${op.id} completed`)
    } catch (error: any) {
      // Failure - increment retry count
      await incrementRetryCount(op.id)
      failed++

      const errorMsg = error.message || 'Unknown error'
      errors.push({ operation: op, error: errorMsg })

      logger.error(`[OfflineQueue] ✗ Operation ${op.id} failed:`, errorMsg)

      // Remove if exceeded max retries (5)
      if (op.retryCount >= 5) {
        await removeFromQueue(op.id)
        logger.debug(`[OfflineQueue] Removed ${op.id} after 5 failed attempts`)
      }
    }
  }

  return { successful, failed, errors }
}

// Listen for online event and auto-sync
let isListening = false

export function startAutoSync(): void {
  if (isListening) return

  isListening = true

  window.addEventListener('online', () => {
    logger.debug('[OfflineQueue] Back online, processing queue...')

    processQueue()
      .then(result => {
        if (result.successful > 0) {
          showToast(
            `Synced ${result.successful} offline ${result.successful === 1 ? 'change' : 'changes'}`,
            'success'
          )
        }

        if (result.failed > 0) {
          showToast(
            `${result.failed} ${result.failed === 1 ? 'change' : 'changes'} failed to sync`,
            'error'
          )
        }
      })
  })

  // Also check on page visibility change (user returns to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && navigator.onLine) {
      getQueueCount()
        .then(count => {
          if (count > 0) {
            logger.debug('[OfflineQueue] Tab visible and online, processing queue...')
            return processQueue()
          }
        })
    }
  })
}

// Check if we're currently offline
export function isOffline(): boolean {
  return !navigator.onLine
}

// Wrapper to execute operation or queue if offline
export async function executeOrQueue<T>(
  operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retryCount'>,
  onlineExecutor: () => Promise<T>
): Promise<T | null> {
  if (isOffline()) {
    await queueOperation(operation)
    return null
  }

  try {
    return await onlineExecutor()
  } catch (error) {
    // If failed due to network, queue it
    if (error instanceof TypeError && error.message.includes('fetch')) {
      logger.debug('[OfflineQueue] Network error, queueing operation')
      await queueOperation(operation)
      return null
    }
    throw error
  }
}
